import type { Template } from '@canvas/shared-types';

type SeedTemplate = Pick<
  Template,
  | 'templateId'
  | 'name'
  | 'description'
  | 'width'
  | 'height'
  | 'category'
  | 'difficulty'
  | 'tags'
  | 'colorPalette'
  | 'isFeatured'
  | 'license'
  | 'defaultContent'
> & { audience: 'student' | 'professional' | 'all' };

function blank(width: number, height: number, objects: Record<string, unknown>[] = []) {
  return {
    version: '5.3.0',
    objects,
    width,
    height,
    background: '#ffffff',
  };
}

function rect(opts: Record<string, unknown>) {
  return { type: 'rect', originX: 'left', originY: 'top', ...opts };
}

function text(opts: Record<string, unknown>) {
  return {
    type: 'i-text',
    originX: 'left',
    originY: 'top',
    fontFamily: 'Outfit, Arial, sans-serif',
    ...opts,
  };
}

export const TEMPLATE_CATALOG: SeedTemplate[] = [
  {
    templateId: 'tpl-lecture-slide',
    name: 'Lecture slide',
    description: 'Clean title slide for class presentations',
    width: 1920,
    height: 1080,
    category: 'presentation',
    difficulty: 'beginner',
    tags: ['school', 'deck', 'student'],
    audience: 'student',
    colorPalette: ['#0d7377', '#152033', '#eef2f6'],
    isFeatured: true,
    license: 'free',
    defaultContent: blank(1920, 1080, [
      rect({ left: 0, top: 0, width: 1920, height: 1080, fill: '#eef2f6' }),
      rect({ left: 0, top: 0, width: 24, height: 1080, fill: '#0d7377' }),
      text({
        left: 120,
        top: 360,
        text: 'Lecture title',
        fontSize: 72,
        fontWeight: '700',
        fill: '#152033',
      }),
      text({
        left: 120,
        top: 460,
        text: 'Course · Week 1 · Your name',
        fontSize: 28,
        fill: '#45566e',
      }),
    ]),
  },
  {
    templateId: 'tpl-academic-poster',
    name: 'Academic poster',
    description: 'Research poster layout with header band',
    width: 2480,
    height: 3508,
    category: 'poster',
    difficulty: 'intermediate',
    tags: ['research', 'student', 'print'],
    audience: 'student',
    colorPalette: ['#1b4d89', '#ffffff', '#e3e9f0'],
    isFeatured: true,
    license: 'free',
    defaultContent: blank(2480, 3508, [
      rect({ left: 0, top: 0, width: 2480, height: 420, fill: '#1b4d89' }),
      text({
        left: 120,
        top: 140,
        text: 'Project title',
        fontSize: 96,
        fontWeight: '700',
        fill: '#ffffff',
      }),
      text({
        left: 120,
        top: 280,
        text: 'Authors · Institution · Year',
        fontSize: 36,
        fill: '#d4e0f0',
      }),
      rect({ left: 120, top: 520, width: 1080, height: 900, fill: '#e3e9f0' }),
      rect({ left: 1280, top: 520, width: 1080, height: 900, fill: '#e3e9f0' }),
      text({
        left: 160,
        top: 560,
        text: 'Abstract',
        fontSize: 42,
        fontWeight: '700',
        fill: '#152033',
      }),
      text({
        left: 1320,
        top: 560,
        text: 'Methods',
        fontSize: 42,
        fontWeight: '700',
        fill: '#152033',
      }),
    ]),
  },
  {
    templateId: 'tpl-linkedin-banner',
    name: 'LinkedIn banner',
    description: 'Professional profile cover with headline space',
    width: 1200,
    height: 627,
    category: 'banner',
    difficulty: 'beginner',
    tags: ['career', 'professional'],
    audience: 'professional',
    colorPalette: ['#152033', '#0d7377', '#ffffff'],
    isFeatured: true,
    license: 'free',
    defaultContent: blank(1200, 627, [
      rect({ left: 0, top: 0, width: 1200, height: 627, fill: '#152033' }),
      rect({ left: 0, top: 500, width: 1200, height: 127, fill: '#0d7377' }),
      text({
        left: 64,
        top: 200,
        text: 'Your professional headline',
        fontSize: 44,
        fontWeight: '700',
        fill: '#ffffff',
      }),
      text({
        left: 64,
        top: 270,
        text: 'Role · Specialty · Open to work',
        fontSize: 22,
        fill: '#d0dae6',
      }),
    ]),
  },
  {
    templateId: 'tpl-youtube-thumb',
    name: 'YouTube thumbnail',
    description: 'Bold thumbnail for courses and explainers',
    width: 1280,
    height: 720,
    category: 'social-media',
    difficulty: 'beginner',
    tags: ['video', 'professional', 'content'],
    audience: 'professional',
    colorPalette: ['#b54708', '#152033', '#ffffff'],
    isFeatured: false,
    license: 'free',
    defaultContent: blank(1280, 720, [
      rect({ left: 0, top: 0, width: 1280, height: 720, fill: '#1c2430' }),
      rect({ left: 64, top: 64, width: 520, height: 592, fill: '#b54708' }),
      text({
        left: 620,
        top: 240,
        text: 'Watch this\nbefore class',
        fontSize: 56,
        fontWeight: '700',
        fill: '#ffffff',
      }),
    ]),
  },
  {
    templateId: 'tpl-instagram-square',
    name: 'Square social post',
    description: '1080² post for announcements and portfolio',
    width: 1080,
    height: 1080,
    category: 'social-media',
    difficulty: 'beginner',
    tags: ['social', 'all'],
    audience: 'all',
    colorPalette: ['#0d7377', '#eef2f6', '#152033'],
    isFeatured: true,
    license: 'free',
    defaultContent: blank(1080, 1080, [
      rect({ left: 0, top: 0, width: 1080, height: 1080, fill: '#0d7377' }),
      rect({ left: 80, top: 80, width: 920, height: 920, fill: '#eef2f6' }),
      text({
        left: 140,
        top: 420,
        text: 'Announce something\nworth sharing',
        fontSize: 52,
        fontWeight: '700',
        fill: '#152033',
      }),
    ]),
  },
  {
    templateId: 'tpl-certificate',
    name: 'Certificate',
    description: 'Completion certificate with border frame',
    width: 1920,
    height: 1358,
    category: 'invitation',
    difficulty: 'beginner',
    tags: ['award', 'student'],
    audience: 'student',
    colorPalette: ['#1b4d89', '#c9a227', '#ffffff'],
    isFeatured: false,
    license: 'free',
    defaultContent: blank(1920, 1358, [
      rect({ left: 0, top: 0, width: 1920, height: 1358, fill: '#ffffff' }),
      rect({
        left: 48,
        top: 48,
        width: 1824,
        height: 1262,
        fill: 'transparent',
        stroke: '#1b4d89',
        strokeWidth: 8,
      }),
      text({
        left: 200,
        top: 280,
        text: 'Certificate of Completion',
        fontSize: 56,
        fontWeight: '700',
        fill: '#1b4d89',
      }),
      text({
        left: 200,
        top: 520,
        text: 'Presented to',
        fontSize: 24,
        fill: '#7b8aa0',
      }),
      text({
        left: 200,
        top: 580,
        text: 'Student Name',
        fontSize: 64,
        fontWeight: '700',
        fill: '#152033',
      }),
      text({
        left: 200,
        top: 780,
        text: 'For successfully completing the course',
        fontSize: 26,
        fill: '#45566e',
      }),
    ]),
  },
];
